import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { Sparkline } from './charts';
import { photoUrl } from './ReportFields';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const pl = (n, d = 1) => (n == null ? '—' : Number(n).toFixed(d).replace('.', ',').replace(/,0$/, ''));
const YN = { tak: 'tak', czesciowo: 'częściowo', nie: 'nie' };
const MEAS = [['waist', 'talia'], ['hips', 'biodra'], ['chest', 'klatka'], ['arm', 'ramię'], ['thigh', 'udo']];

export const weekRange = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 6);
  const f = (x) => `${x.getDate()}.${String(x.getMonth() + 1).padStart(2, '0')}`;
  return `${f(d)}–${f(end)}`;
};

// Value with the change vs the previous report ("82,4 kg −0,6").
const Delta = ({ label, value, prev, unit = '', digits = 1, goodDown }) => {
  if (value == null) return null;
  const diff = prev != null ? value - prev : null;
  const tone = diff == null || Math.abs(diff) < 1e-9 ? 'text-[var(--text-muted)]'
    : (goodDown == null ? 'text-[var(--text-muted)]' : (diff < 0) === goodDown ? 'text-[#22c55e]' : 'text-[#f87171]');
  return (
    <div className="glass-soft rounded-2xl px-3 py-2.5">
      <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{label}</p>
      <p className="text-base font-bold">{pl(value, digits)}{unit}
        {diff != null && Math.abs(diff) > 1e-9 && <span className={`text-xs font-semibold ml-1.5 ${tone}`}>{diff > 0 ? '+' : '−'}{pl(Math.abs(diff), digits)}</span>}
      </p>
    </div>
  );
};

const ReportItem = ({ r, prev, open, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState(r.trainer_comment || '');
  const [saving, setSaving] = useState(false);
  const p = prev || {};
  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/weekly-reports/${r.id}/comment`, { comment }, { withCredentials: true });
      onUpdated(res.data);
      setEditing(false);
      toast.success('Komentarz zapisany — klient go zobaczy');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się zapisać komentarza');
    } finally {
      setSaving(false);
    }
  };
  const photos = Object.entries(r.photos || {}).filter(([, id]) => id);
  return (
    <details open={open} className="glass-soft rounded-2xl px-4 py-3" data-testid={`weekly-report-${r.id}`}>
      <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
        <span className="font-semibold text-sm whitespace-nowrap">{weekRange(r.week_start)}</span>
        <span className="flex flex-wrap justify-end gap-1.5 min-w-0">
          {r.questions && <span className="chip text-[11px] border-[#C9A84C]/50">pytanie</span>}
          <span className="chip text-[11px]">{r.trainer_comment ? 'skomentowany' : 'do komentarza'}</span>
        </span>
      </summary>
      <div className="mt-3 flex flex-col gap-3">
        <p className="text-xs text-[var(--text-muted)]">
          Treningi: {r.auto?.journal_workouts ?? 0} w aplikacji{r.auto?.trainer_sessions ? `, ${r.auto.trainer_sessions} z trenerem` : ''}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Delta label="Waga" value={r.weight} prev={p.weight} unit=" kg" goodDown />
          {MEAS.map(([k, l]) => <Delta key={k} label={l} value={r.measurements?.[k]} prev={p.measurements?.[k]} unit=" cm" goodDown={k === 'waist' || k === 'hips' ? true : null} />)}
          <Delta label="Energia" value={r.energy} prev={p.energy} digits={0} goodDown={false} />
          <Delta label="Regeneracja" value={r.recovery} prev={p.recovery} digits={0} goodDown={false} />
          <Delta label="Sen" value={r.sleep_hours} prev={p.sleep_hours} unit=" h" goodDown={false} />
          <Delta label="Ocena tygodnia" value={r.training_rating} prev={p.training_rating} digits={0} goodDown={false} />
          <Delta label="Odżywianie" value={r.nutrition_adherence} prev={p.nutrition_adherence} unit="%" digits={0} goodDown={false} />
          <Delta label="Kroki" value={r.steps} prev={p.steps} digits={0} goodDown={false} />
          <Delta label="Stres" value={r.stress} prev={p.stress} digits={0} goodDown />
        </div>
        {(r.protein || r.water) && (
          <p className="text-xs text-[var(--text-muted)]">Białko: {YN[r.protein] || '—'} · Woda: {YN[r.water] || '—'}</p>
        )}
        {r.stress_notes && <p className="text-sm"><span className="text-[var(--text-muted)]">Stres: </span>{r.stress_notes}</p>}
        {r.difficulties && <p className="text-sm"><span className="text-[var(--text-muted)]">Trudności: </span>{r.difficulties}</p>}
        {r.questions && <p className="text-sm font-semibold"><span className="text-[#C9A84C]">Pytania: </span>{r.questions}</p>}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 max-w-sm">
            {photos.map(([k, id]) => (
              <a key={k} href={photoUrl(id)} target="_blank" rel="noopener noreferrer" className="aspect-[3/4] rounded-xl overflow-hidden glass-soft">
                <img src={photoUrl(id)} alt={k} loading="lazy" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        )}
        {!editing && r.trainer_comment && <p className="text-sm text-[#C9A84C]">Ty: „{r.trainer_comment}”</p>}
        {editing ? (
          <div className="flex flex-col gap-2">
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
              placeholder="Odpowiedź na raport — klient zobaczy ją w aplikacji" data-testid="report-comment-input"
              className="bg-[var(--bg-primary)] border-[var(--border-soft)] rounded-xl text-sm" />
            <div className="flex gap-2">
              <button type="button" onClick={save} disabled={saving} className="cta-primary h-9 px-4 rounded-full text-xs font-semibold" data-testid="report-comment-save">
                {saving ? 'Zapisywanie…' : 'Zapisz komentarz'}
              </button>
              <button type="button" onClick={() => setEditing(false)} className="cta-ghost h-9 px-4 rounded-full text-xs font-semibold">Anuluj</button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="cta-ghost h-9 px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5 self-start"
            data-testid="report-comment-open">
            <MessageSquare className="w-3.5 h-3.5" /> {r.trainer_comment ? 'Edytuj komentarz' : 'Odpowiedz'}
          </button>
        )}
      </div>
    </details>
  );
};

// Trainer's client card: the client's weekly reports, newest first, with
// weight and waist trends across reports.
export const WeeklyReportsView = ({ clientId }) => {
  const [reports, setReports] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/clients/${clientId}/weekly-reports`, { withCredentials: true })
      .then((r) => setReports(r.data)).catch(() => setReports([]));
  }, [clientId]);
  if (!reports) return null;
  const chrono = [...reports].reverse();
  const weights = chrono.map((r) => r.weight).filter((v) => v != null);
  const waists = chrono.map((r) => r.measurements?.waist).filter((v) => v != null);
  return (
    <div className="flex flex-col gap-2.5" data-testid="weekly-reports-view">
      <label className="label-text text-[var(--text-muted)]">Raporty tygodniowe</label>
      {reports.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Brak raportów. Raport wypełnia klient z włączonym raportem tygodniowym (sobota–wtorek).</p>
      ) : (
        <>
          {(weights.length > 1 || waists.length > 1) && (
            <div className="flex flex-wrap gap-5 glass-soft rounded-2xl px-4 py-3">
              {weights.length > 1 && (
                <div><p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Waga</p>
                  <Sparkline values={weights} gold /><p className="text-xs">{pl(weights[0])} → {pl(weights.at(-1))} kg</p></div>
              )}
              {waists.length > 1 && (
                <div><p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Talia</p>
                  <Sparkline values={waists} /><p className="text-xs">{pl(waists[0])} → {pl(waists.at(-1))} cm</p></div>
              )}
            </div>
          )}
          {reports.map((r, i) => (
            <ReportItem key={r.id} r={r} prev={reports[i + 1]} open={i === 0}
              onUpdated={(u) => setReports((all) => all.map((x) => (x.id === u.id ? u : x)))} />
          ))}
        </>
      )}
    </div>
  );
};
